/**
 * DiagnosticPanel — pokazuje primary hypothesis (root cause) gdy autopilot wykryje
 * underperformance. Komunikuje user-friendly co psuje + co zrobić.
 */
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Stethoscope, AlertCircle, CheckCircle2, Target, Loader2 } from "lucide-react";
import { useAutopilotDiagnostic } from "@/hooks/useAutopilot";

const HYPOTHESIS_META: Record<
  string,
  { label: string; color: string; severity: "ok" | "warn" | "critical" }
> = {
  audience_too_narrow: {
    label: "Audience za wąska",
    color: "bg-orange-500/20 text-orange-600 border-orange-500/40",
    severity: "warn",
  },
  creative_weak: {
    label: "Creative słaby",
    color: "bg-red-500/20 text-red-600 border-red-500/40",
    severity: "critical",
  },
  landing_page_broken: {
    label: "Landing page",
    color: "bg-amber-500/20 text-amber-600 border-amber-500/40",
    severity: "critical",
  },
  sales_call_problem: {
    label: "Sales call / offer",
    color: "bg-amber-500/20 text-amber-600 border-amber-500/40",
    severity: "critical",
  },
  pixel_audience_drift: {
    label: "Pixel drift",
    color: "bg-orange-500/20 text-orange-600 border-orange-500/40",
    severity: "warn",
  },
  insufficient_data: {
    label: "Za mało danych",
    color: "bg-blue-500/20 text-blue-600 border-blue-500/40",
    severity: "ok",
  },
  performing_normally: {
    label: "Wszystko OK",
    color: "bg-emerald-500/20 text-emerald-600 border-emerald-500/40",
    severity: "ok",
  },
};

const CONFIDENCE_LABEL: Record<string, string> = {
  low: "niska",
  medium: "średnia",
  high: "wysoka",
};

export function DiagnosticPanel({ sessionId }: { sessionId: string }) {
  const { data, isLoading } = useAutopilotDiagnostic(sessionId);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-4 text-sm">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Diagnozuję...
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const meta = HYPOTHESIS_META[data.primaryHypothesis] ?? HYPOTHESIS_META.performing_normally;
  const Icon =
    meta.severity === "ok" ? CheckCircle2 : meta.severity === "warn" ? AlertCircle : Target;
  const m = data.metrics;

  return (
    <Card
      className={
        meta.severity === "critical"
          ? "border-red-500/40"
          : meta.severity === "warn"
            ? "border-amber-500/40"
            : ""
      }
    >
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Stethoscope className="h-4 w-4" /> Diagnoza autopilota
          <Badge variant="outline" className={meta.color}>
            <Icon className="h-3 w-3 mr-1" /> {meta.label}
          </Badge>
          <Badge variant="secondary" className="text-xs">
            confidence: {CONFIDENCE_LABEL[data.confidence] ?? data.confidence}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p>{data.recommendation}</p>

        {data.evidence.length > 0 && (
          <div>
            <div className="text-xs font-medium text-muted-foreground mb-1">
              Dowody (okno {data.windowDays}d):
            </div>
            <div className="space-y-1">
              {data.evidence.map((e, i) => (
                <div key={i} className="text-xs font-mono flex flex-wrap gap-2">
                  <span className="font-medium">{e.metric}:</span>
                  <span>{e.value}</span>
                  <span className="text-muted-foreground">(oczekiwane: {e.expected})</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <Metric label="Spend" value={m.spend.toFixed(2)} />
          <Metric label="Impr." value={m.impressions.toLocaleString()} />
          <Metric label="Clicks" value={m.clicks.toLocaleString()} />
          <Metric label="Conv." value={m.conversions.toLocaleString()} />
          {m.ctr != null && <Metric label="CTR" value={`${m.ctr.toFixed(2)}%`} />}
          {m.cpm != null && <Metric label="CPM" value={m.cpm.toFixed(2)} />}
        </div>

        {data.alternativeHypotheses.length > 0 && (
          <div className="text-xs text-muted-foreground">
            Alternatywne hipotezy:{" "}
            {data.alternativeHypotheses.map((h) => HYPOTHESIS_META[h]?.label ?? h).join(", ")}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded border px-2 py-1 bg-muted/30">
      <div className="text-muted-foreground">{label}</div>
      <div className="font-mono font-medium">{value}</div>
    </div>
  );
}
